import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LandingHeader from "../components/Landing/sections/LandingHeader.jsx";
import LandingNav from "../components/Landing/sections/LandingNav.jsx";
import "../styles/new-landing.css";
import LandingFooter from "../components/Landing/sections/LandingFooter.jsx";
import useRestoreScroll from "../hooks/useRestoreScroll.js";

// FAQ items will be loaded from translations

const Contact = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const isRTL = i18n.dir && i18n.dir() === 'rtl';
  const fontFamily = isArabic ? '"Cairo", sans-serif' : '"Red Hat Display", sans-serif';
  useRestoreScroll();

  // Ensure body overflow is restored when component mounts (fix for scroll issues)
  useEffect(() => {
    // Restore body overflow on mount
    document.body.style.overflow = '';
    document.body.style.overflowY = '';

    // Cleanup: ensure overflow is restored on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.overflowY = '';
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const [openIndex, setOpenIndex] = useState(null);
  const toggleIndex = (i) => {
    setOpenIndex(openIndex === i ? null : i);
  };
  // Helper to detect mobile device
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  const email = "support@sulhafa.com";

  // Get FAQ items from translations
  const faqItems = Array.isArray(t("faq.items", { returnObjects: true }))
    ? t("faq.items", { returnObjects: true })
    : [];


  const handleEmailClick = () => {
    window.location.href = `mailto:${email}`;
  };

  return (
    <>
      <LandingHeader />
      <LandingNav />
      <div className="landing-info-page">
      {/* Contact Section */}
      <section className="contact-section">
        <div className="contact-left">
          <h1 className="contact-title">{t("contactPage.title")}</h1>
          <p className="contact-desc">
            {t("contactPage.description")}
          </p>
          <div className="contact-info-row">
            <button className="contact-info-btn" onClick={handleEmailClick}>
              {email}
            </button>
          </div>
          <div style={{ marginTop: "32px" }}>
            <img
              src="/images/Sulhafa-Community.png"
              alt="Sulhafa Character"
              style={{
                width: "200px",
                borderRadius: "16px",
                transform: "scale(1.2) scaleX(-1)"
              }}
            />
          </div>
        </div>
        <form className="contact-form">
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <h3 style={{
              fontFamily: fontFamily,
              fontWeight: 600,
              fontSize: "1.3rem",
              color: "#222e3a",
              margin: 0
            }}>
              {t("contactPage.form.title")}
            </h3>
            <p style={{
              fontFamily: fontFamily,
              fontSize: "0.95rem",
              color: "#666",
              margin: "8px 0 0 0"
            }}>
              {t("contactPage.form.subtitle")}
            </p>
          </div>
          <div className="form-group">
            <label htmlFor="name">{t("contactPage.form.fields.name.label")}</label>
            <input type="text" id="name" name="name" placeholder={t("contactPage.form.fields.name.placeholder")} />
          </div>
          <div className="form-group">
            <label htmlFor="email">{t("contactPage.form.fields.email.label")}</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder={t("contactPage.form.fields.email.placeholder")}
            />
          </div>
          <div className="form-group">
            <label htmlFor="message">{t("contactPage.form.fields.message.label")}</label>
            <textarea
              id="message"
              name="message"
              placeholder={t("contactPage.form.fields.message.placeholder")}
              rows={4}
            />
          </div>
          <button type="submit" className="contact-submit-btn">
            {t("contactPage.form.submit")}
          </button>
          <div className="contact-form-note">
            {t("contactPage.form.replyNote")}
          </div>
        </form>
      </section>

      {/* FAQ Section Below Contact */}
      <section
        className="faq-section"
        style={{ minHeight: "100vh", background: "#fff" }}
      >
        <div
          className="faq-container"
          style={{
            display: "flex",
            gap: "32px",
            maxWidth: "1200px",
            margin: "0 auto",
            paddingTop: "48px",
          }}
        >
          {/* Left Side - Header and Buttons */}
          <div className="faq-left" style={{ flex: 1 }}>
            <h2
              style={{
                fontFamily: fontFamily,
                fontWeight: 700,
                fontSize: "2.5rem",
                color: "#222e3a",
                marginBottom: "18px",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {t("contactPage.faq.title")}{" "}
              <svg width="28" height="28" viewBox="0 0 512 512" fill="none" style={{ display: "inline-block", verticalAlign: "middle", marginLeft: "6px" }}>
                <path fill="#8E44AC" d="M490.39,182.75c-5.55-13.19-14.77-22.7-26.67-27.49l-.16-.06a46.46,46.46,0,0,0-17-3.2h-.64c-27.24.41-55.05,23.56-69.19,57.61-10.37,24.9-11.56,51.68-3.18,71.64,5.54,13.2,14.78,22.71,26.73,27.5l.13.05a46.53,46.53,0,0,0,17,3.2c27.5,0,55.6-23.15,70-57.65C497.65,229.48,498.78,202.72,490.39,182.75Z"/>
                <path fill="#8E44AC" d="M381.55,329.61c-15.71-9.44-30.56-18.37-40.26-34.41C314.53,250.8,298.37,224,256,224s-58.57,26.8-85.39,71.2c-9.72,16.06-24.6,25-40.36,34.48-18.07,10.86-36.74,22.08-44.8,44.16a66.93,66.93,0,0,0-4.65,25c0,35.95,28,65.2,62.4,65.2,17.75,0,36.64-6.15,56.63-12.66,19.22-6.26,39.09-12.73,56.27-12.73s37,6.47,56.15,12.73C332.2,457.85,351,464,368.8,464c34.35,0,62.3-29.25,62.3-65.2a67,67,0,0,0-4.75-25C418.29,351.7,399.61,340.47,381.55,329.61Z"/>
                <path fill="#8E44AC" d="M150,188.85c11.9,14.93,27,23.15,42.52,23.15a42.88,42.88,0,0,0,6.33-.47c32.37-4.76,52.54-44.26,45.92-90C242,102.3,234.6,84.39,224,71.11,212.12,56.21,197,48,181.49,48a42.88,42.88,0,0,0-6.33.47c-32.37,4.76-52.54,44.26-45.92,90C132,157.67,139.4,175.56,150,188.85Z"/>
                <path fill="#8E44AC" d="M313.16,211.53a42.88,42.88,0,0,0,6.33.47c15.53,0,30.62-8.22,42.52-23.15,10.59-13.29,17.95-31.18,20.75-50.4h0c6.62-45.72-13.55-85.22-45.92-90a42.88,42.88,0,0,0-6.33-.47C315,48,299.88,56.21,288,71.11c-10.6,13.28-18,31.19-20.76,50.44C260.62,167.27,280.79,206.77,313.16,211.53Z"/>
                <path fill="#8E44AC" d="M111.59,308.8l.14-.05c11.93-4.79,21.16-14.29,26.69-27.48,8.38-20,7.2-46.75-3.15-71.65C120.94,175.16,92.85,152,65.38,152a46.4,46.4,0,0,0-17,3.2l-.14.05C36.34,160,27.11,169.54,21.58,182.73c-8.38,20-7.2,46.75,3.15,71.65C39.06,288.84,67.15,312,94.62,312A46.4,46.4,0,0,0,111.59,308.8Z"/>
              </svg>
            </h2>
            <p
              style={{
                fontFamily: fontFamily,
                fontSize: "1.1rem",
                color: "#222e3a",
                marginBottom: "32px",
                maxWidth: "420px",
                lineHeight: "1.6",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              {t("contactPage.faq.subtitle")}
            </p>

            <img
              src="/images/Sulhafa-Main.png"
              alt="Sulhafa Character"
              style={{
                width: "280px",
                marginTop: "24px",
                borderRadius: "20px",
                transform: "scale(1.8) scaleX(-1)"
              }}
            />
          </div>
          {/* Right Side - Questions */}
          <div className="faq-right" style={{ flex: 1.2 }}>
            <div
              className="faq-questions-container"
              style={{
                background: "#fff",
                borderRadius: "18px",
                boxShadow: "0 2px 16px rgba(48,62,78,0.08)",
                padding: "32px 32px 24px 32px",
                minWidth: "340px",
                maxWidth: "480px",
                direction: isRTL ? "rtl" : "ltr",
              }}
            >
              <div className="faq-items">
                {faqItems.map((item, i) => (
                  <div
                    key={i}
                    className={`faq-item${openIndex === i ? " open" : ""}`}
                    style={{
                      borderBottom: "1px solid #f0f2f5",
                      padding: "12px 0",
                    }}
                  >
                    <button
                      className="faq-question"
                      onClick={() => toggleIndex(i)}
                      aria-expanded={openIndex === i}
                      style={{
                        background: "none",
                        border: "none",
                        width: "100%",
                        textAlign: isRTL ? "right" : "left",
                        fontFamily: fontFamily,
                        fontWeight: 500,
                        fontSize: "1.08rem",
                        color: "#222e3a",
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexDirection: isRTL ? "row-reverse" : "row",
                        direction: isRTL ? "rtl" : "ltr",
                      }}
                    >
                      <span style={{
                        textAlign: isRTL ? "right" : "left",
                        direction: isRTL ? "rtl" : "ltr",
                        flex: 1,
                        paddingRight: isRTL ? "0" : "8px",
                        paddingLeft: isRTL ? "8px" : "0",
                      }}>{typeof item === 'object' ? item.q : item}</span>
                      <svg
                        className="toggle-icon"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        {openIndex === i ? (
                          <path
                            d="M18 6L6 18M6 6l12 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        ) : (
                          <>
                            <line
                              x1="12"
                              y1="5"
                              x2="12"
                              y2="19"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                            <line
                              x1="5"
                              y1="12"
                              x2="19"
                              y2="12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </>
                        )}
                      </svg>
                    </button>
                    {openIndex === i && (
                      <div
                        className="faq-answer"
                        style={{
                          marginTop: "8px",
                          fontSize: "1rem",
                          color: "#444",
                          fontFamily: fontFamily,
                          textAlign: isRTL ? "right" : "left",
                          direction: isRTL ? "rtl" : "ltr",
                        }}
                      >
                        <p style={{
                          textAlign: isRTL ? "right" : "left",
                          direction: isRTL ? "rtl" : "ltr",
                          margin: 0,
                        }}>{typeof faqItems[openIndex] === 'object' && faqItems[openIndex]?.a ? faqItems[openIndex].a : t("contactPage.faq.answerComingSoon")}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>
      <LandingFooter />
    </>
  );
};

export default Contact;
